import { TourProvider, useTour, type StepType } from "@reactour/tour";
import "@reactour/tour/dist/index.css";
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { isOnboardingGuideEnabled } from "@/constants/featureFlags";
import { useOnboardingTourStatus } from "@/hooks/useOnboardingTourStatus";

/** 吹き出し本文（スクリーンショットのデザインに合わせた緑背景 + 白文字） */
const BubbleText = ({ children }: { children: ReactNode }): ReactElement => (
  <p className="text-center text-sm font-bold leading-relaxed text-white">{children}</p>
);

// 吹き出し文言・改行はスクリーンショットのデザインに合わせる。
const TOUR_STEPS: StepType[] = [
  {
    selector: '[data-tour="spot-sns"]',
    content: (
      <BubbleText>
        まずは気になるスポットについて
        <br />
        GoogleMapやSNSで詳細を確認しよう！
        <br />
        ワンタップでどのSNSにも飛べる！
      </BubbleText>
    )
  },
  {
    selector: '[data-tour="spot-save"]',
    content: (
      <BubbleText>
        気になったスポットを
        <br />
        1件行きたいに保存してみよう！
        <br />
        スワイプでリストに追加可能！
      </BubbleText>
    )
  },
  {
    selector: '[data-tour="spot-save"]',
    content: (
      <BubbleText>
        気になったスポットを
        <br />
        1件行きたいに保存してみよう！
        <br />
        タップでもリスト追加可能！
      </BubbleText>
    )
  }
];

/**
 * 初回ガイドツアーの Provider。
 *
 * `@reactour/tour` の `TourProvider` をラップし、吹き出しのデザインと
 * ナビゲーションボタン（shadcn Button）を定義する。フィーチャーフラグに
 * 関わらず常にマウントし、ツアーを開くかどうかは {@link useOnboardingTour}
 * 側で制御する。
 */
export const OnboardingTourProvider = ({ children }: { children: ReactNode }) => {
  const { updateTourStatus } = useOnboardingTourStatus();
  // beforeClose は Tour マウント時にキャプチャされるため、常に最新の
  // updateTourStatus を参照できるよう ref 経由で呼ぶ。
  const updateTourStatusRef = useRef(updateTourStatus);
  useEffect(() => {
    updateTourStatusRef.current = updateTourStatus;
  }, [updateTourStatus]);

  return (
    <TourProvider
      steps={TOUR_STEPS}
      showBadge={false}
      showDots={false}
      showCloseButton
      scrollSmooth
      disableInteraction
      onClickMask={() => {}}
      beforeClose={() => {
        // "×" ボタン・Esc キー・"はじめる" 押下など、ツアーを閉じる全経路で
        // 完了フラグを更新し、再表示を防ぐ。updateTourStatus は内部でエラーを
        // 握りつぶすため fire-and-forget で呼ぶ。
        void updateTourStatusRef.current(true);
      }}
      padding={{ mask: 8, popover: [14, 16] }}
      styles={{
        popover: (base) => ({
          ...base,
          background: "linear-gradient(135deg, #34d399, #10b981)",
          borderRadius: 16,
          padding: 20,
          maxWidth: 320,
          boxShadow: "0 12px 28px -8px rgba(0, 0, 0, 0.35)"
        }),
        maskArea: (base) => ({ ...base, rx: 14 }),
        close: (base) => ({ ...base, color: "#ffffff", right: 12, top: 12, width: 12, height: 12 }),
        controls: (base) => ({ ...base, marginTop: 16, gap: 8 })
      }}
      nextButton={({ currentStep, stepsLength, setCurrentStep, setIsOpen }) => {
        const isLast = currentStep === stepsLength - 1;
        return (
          <Button
            size="sm"
            className="flex-1 rounded-full bg-white px-5 text-emerald-700 hover:bg-white/90 hover:text-emerald-700"
            onClick={() => {
              if (isLast) {
                // 完了フラグの更新は beforeClose に集約している。
                // ここで閉じるとアンマウント時に beforeClose が発火する。
                setIsOpen(false);
              } else {
                setCurrentStep((step) => step + 1);
              }
            }}
          >
            {isLast ? "はじめる" : "次へ"}
          </Button>
        );
      }}
      prevButton={({ currentStep, setCurrentStep }) => (
        <Button
          size="sm"
          variant="ghost"
          className="flex-1 rounded-full px-5 text-white hover:bg-white/15 hover:text-white"
          disabled={currentStep === 0}
          onClick={() => setCurrentStep((step) => Math.max(0, step - 1))}
        >
          戻る
        </Button>
      )}
    >
      {children}
    </TourProvider>
  );
};

/**
 * ホーム画面の初回ガイドツアーを制御するフック。
 *
 * - フィーチャーフラグ（{@link isOnboardingGuideEnabled}）が有効
 * - DBに表示済み記録がない
 * - `ready` が true（スポットカードが描画済みでツアー対象要素が存在する）
 *
 * の全てを満たしたとき、一度だけツアーを開始する。
 * `useTour` を使うため、必ず {@link OnboardingTourProvider} の配下で呼ぶこと。
 *
 * @param ready ツアー対象要素が DOM に存在する状態かどうか
 */
export const useOnboardingTour = (ready: boolean): void => {
  const { setIsOpen, setCurrentStep } = useTour();
  const startedRef = useRef(false);
  const { tourCompleted, isLoading } = useOnboardingTourStatus();

  // ツアー開始処理
  useEffect(() => {
    if (!isOnboardingGuideEnabled) return;
    if (!ready) return;
    if (startedRef.current) return;
    if (isLoading) {
      return; // まだ読み込み中
    }
    if (tourCompleted) {
      return; // すでに完了済み
    }

    const tourElement = document.querySelector('[data-tour="spot-sns"]');
    if (!tourElement) {
      return;
    }

    startedRef.current = true;
    // ツアーを開始
    setCurrentStep(0);
    setIsOpen(true);
  }, [ready, tourCompleted, isLoading, setIsOpen, setCurrentStep]);
};

/**
 * インフォメーションアイコン等から、初回ガイドツアーを「再表示」するためのフック。
 *
 * 完了状態に関わらず最初のステップからツアーを開く。
 * `useTour` を使うため、必ず {@link OnboardingTourProvider} の配下で呼ぶこと。
 *
 * @returns ツアーを再表示する関数
 */
export const useReopenOnboardingTour = (): (() => void) => {
  const { setIsOpen, setCurrentStep } = useTour();
  return useCallback(() => {
    // 対象要素（スポットカード）が無い場合は開かない（吹き出しの位置ずれ防止）
    if (!document.querySelector('[data-tour="spot-sns"]')) return;
    setCurrentStep(0);
    setIsOpen(true);
  }, [setIsOpen, setCurrentStep]);
};
